import React, { useEffect, useState } from "react";
import moment, { Moment } from "moment";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation, withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  GOOGLE_RECAPTCHA_V2_SITE_KEY,
  GOOGLE_RECAPTCHA_V3_SITE_KEY,
} from "src/app.config";

import Button from "@components/shared/Button/Button";
import { CONSTANTS } from "@constants/index";
import { FORM_FIELD, USER_FORM_FIELD } from "@constants/register";
import { dprTerms, legalTerms } from "@constants/urls";
import { CustomFormProps } from "@models/shared/shared.model";
import { RootState } from "@store/index";

import CustomDatePicker from "../FormElements/CustomDatePicker/CustomDatePicker";
import CustomPhoneInput from "../FormElements/CustomPhoneInput/CustomPhoneInput";
import InputField from "../FormElements/InputField/InputField";
import SelectBoxField from "../FormElements/SelectBoxField/SelectBoxField";
const CustomFormComponent: React.FC<CustomFormProps> = ({
  activeFields,
  closeForm,
  control,
  enableRecaptcha = false,
  formData,
  isEdit = false,
  isInvitation = false,
  recaptchaConfig,
  registerOptions,
}) => {
  const { errors, handleSubmit, isSubmitButtonDisabled, register } = formData;

  const {
    gToken,
    onV2RecaptchaChange,
    recaptchaV2Ref,
    recaptchaV3Ref,
    useV2Recaptcha,
  } = recaptchaConfig || {};

  const { t } = useTranslation();
  const [acceptTerms, setAcceptTerms] = useState({
    gdpr: false,
    legal: false,
  });
  const [disabledSubmitButton, setDisabledSubmitButton] = useState(false);
  const submitButtonClassName = isEdit
    ? "btn fg-btn fg-btn-filled-darkblue mt-5 mt-md-0"
    : "btn fg-btn fg-btn-filled-darkblue w-100";
  const submitButtonLabel = isEdit
    ? t("userProfile.saveChanges")
    : t("completeRegistration");
  const [isFetching] = useState(false);
  const isValidDate = (current: Moment) => {
    return current.isSameOrBefore(moment());
  };

  const { selectedLanguage } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    if (useV2Recaptcha && !gToken) setDisabledSubmitButton(true);
  }, [useV2Recaptcha, gToken]);

  return (
    <form onSubmit={handleSubmit} data-testid="customFormTest">
      <div className="row">
        {activeFields.map(field => (
          <div className={`col-sm-12 ${field.colWidth}`} key={field.name}>
            {field.type === FORM_FIELD.INPUT_FIELD && (
              <div
                className={`form-floating mb-3 fg-input-with-icon ${
                  errors[field.name]?.message ? "fg-is-invalid" : ""
                }`}
              >
                <InputField
                  className={`form-control ${
                    errors[field.name] ? "is-invalid" : ""
                  }`}
                  id={`fg-booking-custom-form-field-${field.name}`}
                  label={field.label_name}
                  name={field.name}
                  register={register}
                  registerOptions={registerOptions}
                  errors={errors as Record<string, { message: string }>}
                  type={field.type}
                  disabled={
                    field.name === USER_FORM_FIELD.EMAIL &&
                    (isEdit || isInvitation)
                  }
                  maxLength={field.maxLength}
                />
              </div>
            )}
            {field.type === FORM_FIELD.DATE_FIELD && (
              <div
                className={`form-floating mb-3 fg-input-with-icon ${
                  errors[field.name]?.message ? "fg-is-invalid" : ""
                }`}
              >
                <CustomDatePicker
                  className={`form-control ${
                    errors[field.name] ? "is-invalid" : ""
                  }`}
                  control={control}
                  errors={errors}
                  id={`fg-booking-custom-form-field-${field.name}`}
                  isValidDate={isValidDate}
                  label={field.label_name}
                  name={field.name}
                  registerOptions={registerOptions}
                />
              </div>
            )}
            {field.type === FORM_FIELD.SELECT_FIELD && (
              <div className="form-floating">
                <SelectBoxField
                  className={`form-control form-select ${
                    errors[field.name] ? "is-invalid" : ""
                  }`}
                  id={`fg-booking-custom-form-field-${field.label_name}`}
                  label={field.label_name}
                  name={field.name}
                  register={register}
                  registerOptions={registerOptions}
                  errors={errors as Record<string, { message: string }>}
                  optionsData={field.options || []}
                />
              </div>
            )}
            {field.type === FORM_FIELD.PHONE_FIELD && (
              <div className="form-floating">
                <CustomPhoneInput
                  className={`form-control ${
                    errors[field.name] ? "is-invalid" : ""
                  }`}
                  control={control}
                  errors={errors}
                  id={`fg_phoneInput_${field.name}`}
                  label={field.label_name}
                  max={CONSTANTS.PHONE_INPUT.MAX_LENGTH}
                  name={field.name}
                  registerOptions={registerOptions}
                />
              </div>
            )}
          </div>
        ))}
        {!isEdit && (
          <>
            <div className="col-sm-12 col-md-12">
              <div className="switch-group  pb-0">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="legalTerms"
                    onChange={e => {
                      setAcceptTerms({
                        ...acceptTerms,
                        legal: e.target.checked,
                      });
                    }}
                  />
                  <label className="form-check-label" htmlFor="legalTerms">
                    {t("iAcceptTheFairgateLegalTerm")}
                    <a href={legalTerms} target="_blank" rel="noreferrer">
                      {t("readHere")}
                    </a>
                  </label>
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="gdpr"
                    onChange={e => {
                      setAcceptTerms({
                        ...acceptTerms,
                        gdpr: e.target.checked,
                      });
                    }}
                  />
                  <label className="form-check-label" htmlFor="gdpr">
                    {t("iAcceptTheFairgateGDPR")}
                    <a href={dprTerms} target="_blank" rel="noreferrer">
                      {t("readHere")}
                    </a>
                  </label>
                </div>
              </div>
            </div>
            <div className="form-text mb-2">{t("emailInputSubHeading")}</div>
          </>
        )}
        {enableRecaptcha && (
          <>
            {!useV2Recaptcha && recaptchaV3Ref && (
              <ReCAPTCHA
                sitekey={GOOGLE_RECAPTCHA_V3_SITE_KEY}
                ref={recaptchaV3Ref}
                hl={selectedLanguage}
                size="invisible"
              />
            )}
            {useV2Recaptcha && recaptchaV2Ref && (
              <div className="my-2">
                <ReCAPTCHA
                  sitekey={GOOGLE_RECAPTCHA_V2_SITE_KEY}
                  ref={recaptchaV2Ref}
                  hl={selectedLanguage}
                  onChange={token => {
                    onV2RecaptchaChange?.(token ?? "");
                    setDisabledSubmitButton(false);
                  }}
                  onErrored={() => onV2RecaptchaChange?.("")}
                  onExpired={() => onV2RecaptchaChange?.("")}
                  size="normal"
                />
              </div>
            )}
          </>
        )}

        <div className="col-sm-12">
          <Button
            className={submitButtonClassName}
            label={submitButtonLabel}
            disabled={
              ((!acceptTerms.gdpr || !acceptTerms.legal) && !isEdit) ||
              isFetching ||
              isSubmitButtonDisabled ||
              (disabledSubmitButton && useV2Recaptcha)
            }
            testid="form-submit-button"
          />
          {isEdit && (
            <button
              className="btn fg-btn fg-btn-grey"
              onClick={e => {
                e.preventDefault();
                if (closeForm) closeForm();
              }}
              data-testid="form-discard-button"
            >
              {t("userProfile.discard")}
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

const CustomForm = withTranslation()(CustomFormComponent);

export default CustomForm;
